/* eslint-disable max-lines-per-function */
import React, { PropsWithChildren } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import { bindHover, bindMenu, bindTrigger } from "material-ui-popup-state";
import { usePopupState } from "material-ui-popup-state/hooks";
import HoverMenu from "material-ui-popup-state/HoverMenu";
import { ListItem } from "@mui/material";
import { Viewer, ScheduledUpdate } from "../auth/types";
import { AppLink, headerButtonStyles, isLinkActive } from "./navigation";
import { useMobileOpen } from "./mobile-open-context";
import { visibleNavLists } from "./list-visibility";

type Props = {
  position?: "left" | "right";
  viewer?: Viewer;
  href: string;
  waitlist?: boolean;
  mobile?: boolean;
  children: string;
}

type ListItem = {
  id: string;
  name: string;
  showsInAppList: boolean;
  scheduledUpdate: ScheduledUpdate | null;
}
type SubMenuProps = {
  mobile?: boolean;
  position?: "left" | "right";
  lists: ListItem[],
  href: string;
}

const SubMenuItems: React.FC<{
  href: string;
  lists: ListItem[];
  popupState: ReturnType<typeof usePopupState>
}> = ({ href, lists, popupState }) => {
  const navigate = useNavigate();
  const { handleClose } = useMobileOpen();
  const includeAll = href === "/dash/manager/lists";
  const sortedLists = lists.sort((a, b) => a.name.localeCompare(b.name));
  const listOptions = includeAll ? [{ id: "all", name: "All" }, ...sortedLists] : sortedLists;

  return (<>{
    listOptions.map(({ id, name }) => {
      const link = `${href}/${id}`;
      const isActive = location.pathname.match(link);
      return (
        <MenuItem
          selected={!!isActive}
          href={link}
          key={id}
          onClick={() => {
            popupState.close();
            handleClose();
            navigate(link);
          }}
        >{name}</MenuItem>
      );
    })}</>);
};

const SubMenuPopupState: React.FC<PropsWithChildren<SubMenuProps>> = ({ mobile = false, lists, href, children, position = "right" }) => {
  const location = useLocation();
  const popupState = usePopupState({ variant: "popover", popupId: `sub-menu-${href.replace("/", "-")}` });

  return mobile ? (
    <>
      <a href="#">
        <MenuItem
          selected={isLinkActive(location.pathname, href)}
          {...bindTrigger(popupState)}
        >
          {children}
        </MenuItem>
      </a>
      <Menu {...bindMenu(popupState)}
        transformOrigin={{
          vertical: "bottom",
          horizontal: position,
        }}>
        <SubMenuItems href={href} lists={lists} popupState={popupState} />
      </Menu>
    </>
  ) : (
    <>
      <Button
        style={{ ...headerButtonStyles, color: isLinkActive(location.pathname, href) ? "#37B4C9" : "white" }}
        color="inherit"
        {...bindHover(popupState)}
      >
        {children}
      </Button>
      <HoverMenu {...bindMenu(popupState)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: position,
        }}
        transformOrigin={{
          vertical: 0,
          horizontal: position,
        }}>
        <SubMenuItems href={href} lists={lists} popupState={popupState} />
      </HoverMenu>
    </>
  );
};

export const ListsNavItem: React.FC<Props> = ({ mobile = false, viewer, children, href, waitlist = false, position = "right" }) => {
  const lists = visibleNavLists(viewer?.lists, waitlist);

  const listsLength = lists.length;
  if (listsLength > 1) {
    return <SubMenuPopupState
      lists={lists}
      href={href}
      position={position}
      mobile={mobile}
    >{children}</SubMenuPopupState>;
  }

  return (<AppLink href={href}>{children}</AppLink>);
};
